import { z } from 'zod';

// Advisor evaluation (spec: docs/plans/advisor-evaluation-spec.md).
// Criteria are evaluated per conversation (episode) and persisted as criterion results:
// observed event → evaluated value → result. Deterministic metrics stay authoritative; AI criteria judge
// semantics and reference persisted Conversation Intelligence findings. Unknown is never 0.

// ───────── Enumerations ─────────

/** Result of one criterion evaluation. pending = not decidable yet; unknown = cannot be determined (coverage). */
export const CRITERION_RESULTS = ['passed', 'partial', 'failed', 'not_applicable', 'pending', 'unknown'] as const;
export const CriterionResultValueSchema = z.enum(CRITERION_RESULTS);
export type CriterionResultValue = z.infer<typeof CriterionResultValueSchema>;

export const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export const SeveritySchema = z.enum(SEVERITIES);
export type Severity = z.infer<typeof SeveritySchema>;
export const SEVERITY_RANK: Record<Severity, number> = { low: 1, medium: 2, high: 3, critical: 4 };

export const CRITERION_SOURCES = ['metric', 'ai'] as const;
export const CriterionSourceSchema = z.enum(CRITERION_SOURCES);
export type CriterionSource = z.infer<typeof CriterionSourceSchema>;

/**
 * Where the lead clock starts. whatsapp_first_message = the lead's first inbound WhatsApp message of the
 * conversation (or the advisor's first outbound when the advisor initiated). CRM sources are reserved.
 */
export const LEAD_CLOCK_SOURCES = ['whatsapp_first_message', 'crm_created', 'crm_assigned'] as const;
export const LeadClockSourceSchema = z.enum(LEAD_CLOCK_SOURCES);
export type LeadClockSource = z.infer<typeof LeadClockSourceSchema>;

/** SLA bands (§1): 🟢 excellent · 🟡 acceptable · 🟠 slow · 🔴 critical. Within SLA = excellent or acceptable. */
export const SLA_BANDS = ['excellent', 'acceptable', 'slow', 'critical'] as const;
export const SlaBandSchema = z.enum(SLA_BANDS);
export type SlaBand = z.infer<typeof SlaBandSchema>;

/** Follow-up touch timing against the tenant cadence (§5). */
export const TOUCH_TIMINGS = ['early', 'on_time', 'late', 'excess'] as const;
export const TouchTimingSchema = z.enum(TOUCH_TIMINGS);
export type TouchTiming = z.infer<typeof TouchTimingSchema>;

export const INTENT_LEVELS = ['high', 'medium', 'low', 'unknown'] as const;
export const IntentLevelSchema = z.enum(INTENT_LEVELS);
export type IntentLevel = z.infer<typeof IntentLevelSchema>;

/** The six explained score dimensions (§22). */
export const SCORE_DIMENSIONS = ['speed', 'follow_up', 'quality', 'discovery', 'sales_execution', 'discipline'] as const;
export const ScoreDimensionSchema = z.enum(SCORE_DIMENSIONS);
export type ScoreDimension = z.infer<typeof ScoreDimensionSchema>;

// ───────── Criteria registry ─────────

export interface CriterionDef {
  key: string;
  /** Spanish label shown in the product. */
  label: string;
  /** Plain-language description of what passes. */
  description: string;
  /** Section of the product spec. */
  spec: string;
  source: CriterionSource;
  dimension: ScoreDimension;
  /** Relative weight inside its dimension. 0 = informational (shown, never scored). */
  weight: number;
  /** Delivery phase that produces it. */
  phase: 1 | 2;
  /** True when one row exists per sub-event (reply, touch, question, objection) instead of per conversation. */
  perEvent?: boolean;
}

export const CRITERIA = [
  // Speed
  { key: 'first_contact_sla', label: 'Velocidad de primer contacto', description: 'El asesor envía el primer mensaje dentro del SLA desde que llega el lead.', spec: '§1', source: 'metric', dimension: 'speed', weight: 3, phase: 1 },
  { key: 'reply_sla', label: 'Velocidad de respuesta en la conversación', description: 'Cada vez que el prospecto escribe, el asesor responde dentro del SLA.', spec: '§2', source: 'metric', dimension: 'speed', weight: 3, phase: 1, perEvent: true },
  // Follow-up
  { key: 'follow_up_compliance', label: 'Cumplimiento de seguimiento', description: 'Seguimientos realizados contra los esperados por la cadencia mientras el prospecto no responde.', spec: '§4–6', source: 'metric', dimension: 'follow_up', weight: 3, phase: 1, perEvent: true },
  { key: 'touch_cadence', label: 'Cadencia de seguimiento', description: 'Cada seguimiento llega a tiempo: ni demasiado pronto, ni tarde, ni en exceso.', spec: '§5', source: 'metric', dimension: 'follow_up', weight: 2, phase: 1, perEvent: true },
  // Informational: already scored through follow_up_compliance; shown as "leads abandonados tras 1 toque".
  { key: 'single_touch_abandonment', label: 'Abandono tras un solo intento', description: 'El asesor no deja al prospecto después de un único intento sin respuesta.', spec: '§4, §6', source: 'metric', dimension: 'follow_up', weight: 0, phase: 1 },
  { key: 'touch_quality', label: 'Calidad de los seguimientos', description: 'El seguimiento usa contexto, personaliza, da un motivo nuevo para responder y tiene una llamada a la acción clara.', spec: '§7', source: 'ai', dimension: 'follow_up', weight: 2, phase: 2, perEvent: true },
  { key: 'recovery', label: 'Recuperación de la conversación', description: 'El prospecto volvió a responder tras un seguimiento o reactivación.', spec: '§20', source: 'metric', dimension: 'follow_up', weight: 0, phase: 1 },
  // Conversation quality
  { key: 'communication_clarity', label: 'Claridad', description: 'Mensajes claros y fáciles de entender.', spec: '§16', source: 'ai', dimension: 'quality', weight: 2, phase: 2 },
  { key: 'communication_brevity', label: 'Brevedad', description: 'Mensajes con la longitud adecuada para WhatsApp.', spec: '§16', source: 'ai', dimension: 'quality', weight: 1, phase: 2 },
  { key: 'communication_naturalness', label: 'Naturalidad', description: 'Tono natural, no robótico ni de plantilla.', spec: '§16', source: 'ai', dimension: 'quality', weight: 1, phase: 2 },
  { key: 'communication_personalization', label: 'Personalización', description: 'Usa el nombre, el desarrollo, el contexto previo y las necesidades del prospecto.', spec: '§16', source: 'ai', dimension: 'quality', weight: 2, phase: 2 },
  { key: 'communication_spelling', label: 'Ortografía', description: 'Sin errores graves o recurrentes (sin penalizar el estilo natural de WhatsApp).', spec: '§16', source: 'ai', dimension: 'quality', weight: 1, phase: 2 },
  { key: 'communication_professionalism', label: 'Profesionalismo', description: 'Sin tono agresivo, comentarios inapropiados ni exceso de confianza.', spec: '§16', source: 'ai', dimension: 'quality', weight: 2, phase: 2 },
  { key: 'message_density', label: 'Densidad de mensajes', description: 'Sin bombardeo: pocos mensajes consecutivos, sin mensajes demasiado largos ni exceso de información antes de recibir respuesta.', spec: '§17', source: 'metric', dimension: 'quality', weight: 1, phase: 1 },
  { key: 'materials_usage', label: 'Uso de materiales', description: 'Los materiales (brochure, video, ubicación, PDF, links) se envían cuando tienen sentido y no sustituyen una respuesta.', spec: '§18', source: 'ai', dimension: 'quality', weight: 1, phase: 2 },
  // Discovery / qualification
  { key: 'discovery_sufficient', label: 'Calificación suficiente', description: 'El asesor obtuvo suficiente contexto (propósito, presupuesto, forma de pago, zona, timing…) para avanzar la venta.', spec: '§12', source: 'ai', dimension: 'discovery', weight: 3, phase: 2 },
  { key: 'question_quality', label: 'Calidad de las preguntas', description: 'Preguntas relevantes, naturales, una a la vez, sin repetir información ya dada ni interrogar.', spec: '§13', source: 'ai', dimension: 'discovery', weight: 2, phase: 2 },
  { key: 'questions_answered', label: 'Respuesta a preguntas del prospecto', description: 'Cada pregunta del prospecto se responde (no se evade, ignora o cambia de tema).', spec: '§14', source: 'ai', dimension: 'discovery', weight: 3, phase: 2, perEvent: true },
  // Sales execution
  { key: 'objection_handling', label: 'Manejo de objeciones', description: 'Reconoce la objeción, responde específicamente, no evade ni confronta y mantiene el avance.', spec: '§15', source: 'ai', dimension: 'sales_execution', weight: 3, phase: 2, perEvent: true },
  { key: 'appointment_attempt', label: 'Intento de llevar a cita', description: 'Cuando hay interés y contexto, propone una cita con horarios concretos y la confirma.', spec: '§10', source: 'ai', dimension: 'sales_execution', weight: 3, phase: 2 },
  { key: 'hot_lead_without_appointment', label: 'Lead caliente sin cita', description: 'Un prospecto con varias señales de alta intención no se queda sin propuesta de cita.', spec: '§11', source: 'ai', dimension: 'sales_execution', weight: 3, phase: 2 },
  { key: 'next_step_defined', label: 'Próximo paso definido', description: 'La conversación activa termina con una siguiente acción clara.', spec: '§9', source: 'ai', dimension: 'sales_execution', weight: 3, phase: 2 },
  { key: 'closing_outcome', label: 'Cierre de la conversación', description: 'La conversación termina con un resultado claro, no desaparece sin resolución.', spec: '§19', source: 'ai', dimension: 'sales_execution', weight: 1, phase: 2 },
  // Operational discipline
  { key: 'lead_waiting', label: 'Prospecto esperando respuesta', description: 'El último mensaje del prospecto no se queda sin respuesta (conversación abandonada).', spec: '§3', source: 'metric', dimension: 'discipline', weight: 3, phase: 1 },
  { key: 'abandoned_pending_action', label: 'Acción pendiente abandonada', description: 'No queda una pregunta o acción del prospecto pendiente de atender.', spec: '§3', source: 'ai', dimension: 'discipline', weight: 3, phase: 2 },
  { key: 'follow_up_overdue', label: 'Seguimiento vencido', description: 'No hay un seguimiento vencido según la cadencia.', spec: '§5, §24', source: 'metric', dimension: 'discipline', weight: 2, phase: 1 },
  { key: 'stalled_conversation', label: 'Conversación estancada', description: 'La conversación activa no se queda estancada demasiado tiempo.', spec: '§8', source: 'metric', dimension: 'discipline', weight: 2, phase: 1 },
  // Informational
  { key: 'active_time', label: 'Tiempo activo de la conversación', description: 'Duración desde el primer hasta el último mensaje.', spec: '§8', source: 'metric', dimension: 'discipline', weight: 0, phase: 1 },
  { key: 'intent_level', label: 'Intención del prospecto', description: 'Nivel de intención del prospecto (pondera la severidad de los errores). No califica al asesor.', spec: '§21', source: 'ai', dimension: 'sales_execution', weight: 0, phase: 2 },
] as const satisfies readonly CriterionDef[];

export type CriterionKey = (typeof CRITERIA)[number]['key'];
export const CRITERION_KEYS = CRITERIA.map((c) => c.key) as [CriterionKey, ...CriterionKey[]];
export const CriterionKeySchema = z.enum(CRITERION_KEYS);
export const CRITERIA_BY_KEY: Record<CriterionKey, CriterionDef> = Object.fromEntries(CRITERIA.map((c) => [c.key, c])) as Record<CriterionKey, CriterionDef>;

export function criteriaForDimension(d: ScoreDimension): CriterionDef[] {
  return CRITERIA.filter((c) => c.dimension === d);
}

// ───────── Evaluation policy (versioned per tenant) ─────────

const Minutes = z.number().min(0.5).max(7 * 24 * 60);

export const SlaBandsSchema = z
  .object({
    /** Below this many minutes: excellent. */
    excellentMinutes: Minutes,
    /** Below this: acceptable (still within SLA). */
    acceptableMinutes: Minutes,
    /** Below this: slow; at or above: critical. */
    slowMinutes: Minutes,
  })
  .refine((b) => b.excellentMinutes < b.acceptableMinutes && b.acceptableMinutes < b.slowMinutes, {
    message: 'Los umbrales deben ser crecientes',
  });
export type SlaBands = z.infer<typeof SlaBandsSchema>;

export const EvaluationPolicySchema = z.object({
  /** Clock used for SLA bands and waiting times. Business hours by default. */
  responseClock: z.enum(['business', 'elapsed']),
  firstContactSla: SlaBandsSchema,
  replySla: SlaBandsSchema,
  touchCadence: z.object({
    /** Expected delay before each follow-up while the prospect does not answer (hours since the previous touch). */
    followUpDelaysHours: z.array(z.number().min(0.25).max(24 * 60)).min(1).max(12),
    /** A follow-up earlier than delay × (1 − tol) is early; later than its due time + delay × tol is late. */
    tolerancePercent: z.number().min(0).max(100),
  }),
  abandonment: z.object({
    /** A prospect message unanswered for this many (clock) hours makes the conversation abandoned. */
    abandonedAfterHours: z.number().min(0.25).max(24 * 30),
  }),
  density: z.object({
    /** More consecutive advisor messages than this in one touch = bombardeo. */
    maxConsecutiveMessages: z.number().int().min(1).max(50),
    /** Messages longer than this many characters are too long for WhatsApp. */
    longMessageChars: z.number().int().min(100).max(10_000),
    /** More advisor messages than this before the prospect first answers = exceso de información. */
    maxMessagesBeforeReply: z.number().int().min(1).max(100),
  }),
  activity: z.object({
    /** An open conversation without messages for this many hours is stalled. */
    stalledAfterHours: z.number().min(1).max(24 * 30),
    /** An open conversation active longer than this many days without closing is stalled. */
    maxActiveDays: z.number().min(1).max(365),
  }),
  /** Multiplier applied to the severity of failures according to the prospect's intent (§21). */
  intentSeverityWeights: z.object({
    high: z.number().min(0).max(5),
    medium: z.number().min(0).max(5),
    low: z.number().min(0).max(5),
    unknown: z.number().min(0).max(5),
  }),
  /** Minimum high-intent signals to consider a lead hot (§11). */
  hotLeadMinSignals: z.number().int().min(1).max(10),
  /** Dimension weights of the advisor score (§22). Normalized over available dimensions. */
  scoreWeights: z.object({
    speed: z.number().min(0).max(100),
    follow_up: z.number().min(0).max(100),
    quality: z.number().min(0).max(100),
    discovery: z.number().min(0).max(100),
    sales_execution: z.number().min(0).max(100),
    discipline: z.number().min(0).max(100),
  }),
});
export type EvaluationPolicy = z.infer<typeof EvaluationPolicySchema>;

export const DEFAULT_EVALUATION_POLICY: EvaluationPolicy = {
  responseClock: 'business',
  firstContactSla: { excellentMinutes: 5, acceptableMinutes: 15, slowMinutes: 60 },
  replySla: { excellentMinutes: 5, acceptableMinutes: 15, slowMinutes: 60 },
  // Spec §5 example: 1 immediate · 2 same day · 3 +1 day · 4 +2 days · 5 +4 days · 6 +7 days.
  touchCadence: { followUpDelaysHours: [4, 24, 48, 96, 168], tolerancePercent: 50 },
  abandonment: { abandonedAfterHours: 9 },
  density: { maxConsecutiveMessages: 4, longMessageChars: 700, maxMessagesBeforeReply: 6 },
  activity: { stalledAfterHours: 48, maxActiveDays: 14 },
  intentSeverityWeights: { high: 1.5, medium: 1, low: 0.6, unknown: 1 },
  hotLeadMinSignals: 2,
  scoreWeights: { speed: 20, follow_up: 20, quality: 15, discovery: 15, sales_execution: 20, discipline: 10 },
};

/** Parse a stored policy, filling missing sections from the defaults (older rows stay readable). */
export function resolveEvaluationPolicy(raw: unknown): EvaluationPolicy {
  if (raw && typeof raw === 'object') {
    const merged = { ...DEFAULT_EVALUATION_POLICY, ...(raw as Record<string, unknown>) };
    const parsed = EvaluationPolicySchema.safeParse(merged);
    if (parsed.success) return parsed.data;
  }
  return DEFAULT_EVALUATION_POLICY;
}

/** Band for a duration in seconds. */
export function slaBand(seconds: number, bands: SlaBands): SlaBand {
  const m = seconds / 60;
  if (m < bands.excellentMinutes) return 'excellent';
  if (m < bands.acceptableMinutes) return 'acceptable';
  if (m < bands.slowMinutes) return 'slow';
  return 'critical';
}

export function isWithinSla(band: SlaBand): boolean {
  return band === 'excellent' || band === 'acceptable';
}

// ───────── Labels ─────────

export const CRITERION_RESULT_LABELS: Record<CriterionResultValue, string> = {
  passed: 'Cumple',
  partial: 'Parcial',
  failed: 'No cumple',
  not_applicable: 'No aplica',
  pending: 'Pendiente',
  unknown: 'Desconocido',
};

export const SEVERITY_LABELS: Record<Severity, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  critical: 'Crítica',
};

export const SLA_BAND_LABELS: Record<SlaBand, string> = {
  excellent: 'Excelente',
  acceptable: 'Aceptable',
  slow: 'Lento',
  critical: 'Crítico',
};

export const TOUCH_TIMING_LABELS: Record<TouchTiming, string> = {
  early: 'Demasiado pronto',
  on_time: 'A tiempo',
  late: 'Tarde',
  excess: 'Exceso de seguimiento',
};

export const INTENT_LEVEL_LABELS: Record<IntentLevel, string> = {
  high: 'Alta',
  medium: 'Media',
  low: 'Baja',
  unknown: 'Desconocida',
};

export const SCORE_DIMENSION_LABELS: Record<ScoreDimension, string> = {
  speed: 'Velocidad',
  follow_up: 'Seguimiento',
  quality: 'Calidad de conversación',
  discovery: 'Descubrimiento y calificación',
  sales_execution: 'Ejecución comercial',
  discipline: 'Disciplina operativa',
};

export const LEAD_CLOCK_SOURCE_LABELS: Record<LeadClockSource, string> = {
  whatsapp_first_message: 'Primer mensaje de WhatsApp',
  crm_created: 'Creación del lead en CRM',
  crm_assigned: 'Asignación del lead en CRM',
};

export const RESPONSE_CLOCK_LABELS: Record<EvaluationPolicy['responseClock'], string> = {
  business: 'Horario laboral',
  elapsed: 'Tiempo real',
};

// ───────── Wire format ─────────

export const CriterionResultSchema = z.object({
  id: z.string(),
  criterionKey: CriterionKeySchema,
  source: CriterionSourceSchema,
  subjectKey: z.string(),
  touchSeq: z.number().int().nullable(),
  observedEvent: z.string(),
  observedAt: z.string().nullable(),
  result: CriterionResultValueSchema,
  severity: SeveritySchema.nullable(),
  band: z.string().nullable(),
  valueSeconds: z.number().int().nullable(),
  valueNumber: z.number().nullable(),
  value: z.record(z.string(), z.unknown()).nullable(),
  evidenceMessageIds: z.array(z.string()),
  intelligenceFindingIds: z.array(z.string()),
  explanation: z.string().nullable(),
  recommendedAction: z.string().nullable(),
  leadClockSource: LeadClockSourceSchema.nullable(),
  policyVersion: z.number().int(),
  promptVersion: z.string().nullable(),
  computedAt: z.string(),
});
export type CriterionResult = z.infer<typeof CriterionResultSchema>;
